import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import DateFilter from './DateFilter';
import { AttentionFilter, ATTENTION_DOT_COLOR, ATTENTION_FILTERS } from '../standingOrderModel';
import styles from '@/components/filters/FiltersArea.module.scss';
import boardStyles from './FiltersArea.module.scss';

interface FiltersAreaProps {
  dateRange: { from: string; to: string } | null;
  onDateRangeChange: (range: { from: string; to: string } | null) => void;
  attention: Set<AttentionFilter>;
  onToggleAttention: (key: AttentionFilter) => void;
  attentionCounts: Record<AttentionFilter, number>;
  /**
   * The board has not answered yet. The counts are all zero until it does, and "נכשל (0)" is a
   * claim about the fund rather than about the wait - so while this is true the pills carry
   * their name and no number.
   */
  isLoading?: boolean;
  onClearFilters: () => void;
  /**
   * The bank board asks the same questions in the bank's words (HAT-1135): a charge there is
   * «הוחזר», not «נכשל», and what runs out is the order's payments, not a card. A stopped order is
   * «לא פעילה» wherever the board cannot freeze. Keys not given keep the credit board's labels.
   */
  attentionLabelKeys?: Partial<Record<AttentionFilter, string>>;
}

/**
 * The one dimension a coordinator narrows by hand - a period - and then the states they came to
 * the board to find, each one click with its count on it. The pills are the pattern the donations
 * boards already use, so a red dot next to a number means the same thing wherever it appears.
 *
 * There was a payment-method dropdown here too, until the board became a credit board: with
 * one method left it offered a choice between everything and everything. And there was a
 * «הצג גם לא פעילות» switch until HAT-1447: the frozen and the finished orders it held back now
 * have a pill each, and the count on each says what the default view leaves out.
 *
 * HAT-1238: on a phone the attention pills sit behind "עוד", so the date filter and that
 * one chip wrap instead of overlapping or disappearing into a horizontal scroller.
 */
const FiltersArea = ({
  dateRange, onDateRangeChange,
  attention, onToggleAttention, attentionCounts, isLoading = false,
  onClearFilters, attentionLabelKeys,
}: FiltersAreaProps) => {
  const { t } = useTranslation();
  const { isPhone } = useDeviceDetection();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreId = useId();

  const hasActiveFilters = Boolean(dateRange) || attention.size > 0;
  const showAttention = !isPhone || moreOpen || attention.size > 0;

  return (
    <div className={styles.filtersArea}>
      <div className={`${styles.filtersContainer} ${boardStyles.chips}`}>
        <span className={`body-md-regular text-(--font-body-3) mt-1 ${boardStyles.filterLabel}`}>
          {t('filterByLabel')}
        </span>
        <DateFilter dateRange={dateRange} onDateRangeChange={onDateRangeChange} />
        {isPhone && (
          <button
            type="button"
            className={boardStyles.moreToggle}
            onClick={() => setMoreOpen((open) => !open)}
            aria-expanded={showAttention}
            aria-controls={moreId}
            data-active={attention.size > 0}
          >
            {t('standingOrders.moreFilters')}
          </button>
        )}
        <div id={moreId} className={boardStyles.attentionGroup} hidden={!showAttention}>
          {ATTENTION_FILTERS.map((key) => (
            <StatusFilter
              key={key}
              title={attentionLabelKeys?.[key] ?? `standingOrders.attention_${key}`}
              isSelected={attention.has(key)}
              onClick={() => onToggleAttention(key)}
              iconColor={ATTENTION_DOT_COLOR[key]}
              count={isLoading ? undefined : attentionCounts[key]}
            />
          ))}
        </div>
        {hasActiveFilters && (
          <button type="button" className={styles.clearFilters} onClick={onClearFilters}>
            {t('clearFilters')}
          </button>
        )}
      </div>
    </div>
  );
};

export default FiltersArea;
